import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Edit, Trash2, Search, Phone, Mail, MapPin, Building2 } from 'lucide-react';
import api from '../lib/api';

const TYPES = [
  { value: 'cliente', label: 'Cliente' },
  { value: 'fornitore', label: 'Fornitore' },
  { value: 'subappaltatore', label: 'Subappaltatore' },
  { value: 'professionista', label: 'Professionista' },
  { value: 'ente', label: 'Ente/PA' },
  { value: 'altro', label: 'Altro' },
];

const emptyForm = {
  name: '', company: '', role: '', contact_type: 'cliente',
  email: '', phone: '', mobile: '', address: '', city: '',
  province: '', zip_code: '', vat_number: '', fiscal_code: '', notes: ''
};

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('__all__');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (filterType !== '__all__') params.contact_type = filterType;
      const { data } = await api.get('/contacts', { params });
      setContacts(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search, filterType]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ ...emptyForm }); setEditingId(null); setShowForm(true); };
  const openEdit = (c) => {
    setForm({
      name: c.name || '', company: c.company || '', role: c.role || '',
      contact_type: c.contact_type || 'cliente', email: c.email || '',
      phone: c.phone || '', mobile: c.mobile || '', address: c.address || '',
      city: c.city || '', province: c.province || '', zip_code: c.zip_code || '',
      vat_number: c.vat_number || '', fiscal_code: c.fiscal_code || '', notes: c.notes || ''
    });
    setEditingId(c.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await api.put(`/contacts/${editingId}`, form);
      } else {
        await api.post('/contacts', form);
      }
      setShowForm(false);
      load();
    } catch (err) { alert(err.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    try { await api.delete(`/contacts/${id}`); load(); }
    catch (err) { alert('Errore eliminazione'); }
  };

  const typeColor = (t) => {
    const m = { cliente: 'bg-blue-50 text-blue-700 border-blue-200', fornitore: 'bg-green-50 text-green-700 border-green-200', subappaltatore: 'bg-amber-50 text-amber-700 border-amber-200', professionista: 'bg-purple-50 text-purple-700 border-purple-200', ente: 'bg-rose-50 text-rose-700 border-rose-200' };
    return m[t] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contacts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rubrica Contatti</h1>
        <Button onClick={openCreate} data-testid="add-contact-btn"><Plus size={16} className="mr-1" /> Nuovo Contatto</Button>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Cerca per nome, azienda, email, telefono..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="contacts-search" />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-48" data-testid="contacts-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i tipi</SelectItem>
                {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Azienda</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Telefono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Citta</TableHead>
                <TableHead className="w-24">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Nessun contatto trovato</TableCell></TableRow>
              ) : contacts.map(c => (
                <TableRow key={c.id} className="hover:bg-slate-50" data-testid={`contact-row-${c.id}`}>
                  <TableCell className="font-medium">{c.name} {c.role && <span className="text-xs text-slate-400">({c.role})</span>}</TableCell>
                  <TableCell className="text-sm">{c.company || '-'}</TableCell>
                  <TableCell><Badge variant="outline" className={typeColor(c.contact_type)}>{TYPES.find(t => t.value === c.contact_type)?.label || c.contact_type}</Badge></TableCell>
                  <TableCell className="text-sm">{c.phone || c.mobile || '-'}</TableCell>
                  <TableCell className="text-sm">{c.email || '-'}</TableCell>
                  <TableCell className="text-sm">{c.city || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="p-1 h-auto text-blue-600"><Edit size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="p-1 h-auto text-red-600"><Trash2 size={14} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Contatto' : 'Nuovo Contatto'}</DialogTitle>
            <DialogDescription>Compila i dati del contatto</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-1.5"><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" /></div>
            <div className="space-y-1.5"><Label>Azienda</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Ruolo</Label><Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Cellulare</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>P. IVA</Label><Input value={form.vat_number} onChange={(e) => setForm({ ...form, vat_number: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input value={form.fiscal_code} onChange={(e) => setForm({ ...form, fiscal_code: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Citta</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5"><Label>Prov.</Label><Input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} maxLength={2} /></div>
              <div className="space-y-1.5"><Label>CAP</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} maxLength={5} /></div>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={handleSave} disabled={!form.name} data-testid="contact-save-btn">{editingId ? 'Aggiorna' : 'Crea'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
